import {
  getOrgApps,
  getOrganisation,
} from '@/api'
import {
  DEFAULT_APP_NAME,
  DEFAULT_CONFIG,
  DEFAULT_FIELD_MAP_CONFIG,
  DEFAULT_ORG_NAME,
  DEFAULT_SEARCH_CONFIG,
  FIELD_CALCULATIONS_CONFIG_KEY,
  FIELD_CALCULATIONS_NEW_CONFIG_KEY,
  NAME_ID_SEPARATOR,
  ROUTE_SIDEBAR_SETTINGS,
} from '@/constants'
import {
  AppType,
  AppTypes,
  CBVConfig,
  FieldMapItem,
  NodeResponse,
  Path,
  VersionConfig,
  VersionConfigResponse,
} from '@/types'
import mergeOptions from 'merge-options'
import { LevelsHelper } from '@/store/levelsHelper'
import { is } from '@/helpers'

export const getObjectProp = (p: (string | number)[], o: VersionConfig | NodeResponse) =>
  p.reduce((xs, x) =>
    // @ts-ignore
    (xs && xs[x as keyof VersionConfig | keyof NodeResponse]) ? xs[x as keyof VersionConfig | keyof NodeResponse] : null, o)

export function sortByPath (a: {path: Path}, b: {path: Path}, propertyAccessor: { (path: string): number | null }) {
  const aOrder = propertyAccessor(a.toString()) || 0
  const bOrder = propertyAccessor(b.toString()) || 0
  return aOrder - bOrder
}

export function calculateNextIdFromNames (names: string[]): number {
  if (names.length) {
    const existingIds = names.map(name => {
      const parsed = parseInt(name.split(NAME_ID_SEPARATOR).pop() || '0')
      if (isNaN(parsed)) {
        return 0
      }
      return parsed
    })
    return Math.max(...existingIds) + 1
  } else {
    return 1
  }
}

// app
export function getLocalStorage () {
  if (localStorage.getItem('designMode') === null) {
    localStorage.setItem('designMode', ROUTE_SIDEBAR_SETTINGS + '_new')
  }
}

// config
export async function addDefaultConfig (appType: AppType, appId: number, config: VersionConfigResponse) {
  let customConfig = {}
  let cbvConfig = {}
  if (appType === AppTypes.BUBBLES) {
    customConfig = addVisualisationAppCustomConfig(config)
    cbvConfig = getVisualisationAppCBVConfig()
  }

  if (!Array.isArray(config.fieldMap)) {
    config.fieldMap = DEFAULT_FIELD_MAP_CONFIG
  }

  // The last options given to mergeOptions will overwrite earlier options
  const merged = mergeOptions(DEFAULT_CONFIG, customConfig, config, cbvConfig || {}) as VersionConfig

  if (merged.appConfig.appName === DEFAULT_APP_NAME) {
    // Should update appName and orgName on first load only
    // If they didn't save their configs, they means "custom app name" in the config would force their public app to hit our organisation/apps endpoint
    // Therefore they do not have the correct permissions, and get the eternal loading page bug
    const apps = await getOrgApps()
    const app = apps.find(app => app.id === appId)
    if (app) {
      merged.appConfig.appName = app.name
    }
  }

  if (merged.appConfig.orgName === DEFAULT_ORG_NAME) {
    // Should update orgName on first load only
    const org = await getOrganisation()
    merged.appConfig.orgName = org.name
  }

  return merged
}

function addVisualisationAppCustomConfig (config: VersionConfigResponse) {
  // This is used to add new config to existing apps
  // Default config should have all the new config
  // Custom config should be used (reluctantly) to shift values around from the old config to the new
  const customConfig = {
    appConfig: {
      projectLabelField: LevelsHelper.fromFieldMapItems(config.fieldMap).leafLevelField?.column,
    },
    searchSettings: {
      enableSearchByLocation: (config.searchSettings || DEFAULT_SEARCH_CONFIG).enabled,
      enableSearchForProjects: (config.searchSettings || DEFAULT_SEARCH_CONFIG).enabled,
    },
    numericFormatting: {
      locale: config?.appConfig?.numericFormatting?.locale || DEFAULT_CONFIG.numericFormatting.locale,
    },
    monetaryFormatting: {
      currency: config?.appConfig?.numericFormatting?.currency || DEFAULT_CONFIG.monetaryFormatting.currency,
      numDigits: config?.appConfig?.numericFormatting?.numDigits ?? DEFAULT_CONFIG.monetaryFormatting.numDigits,
    },
    bubblesLegends: {
      showLegend: config?.hollowBubbles?.showLegend || DEFAULT_CONFIG.bubblesLegends.showLegend,
      legendTitle: config?.hollowBubbles?.legendTitle || DEFAULT_CONFIG.bubblesLegends.legendTitle,
      legendPosition: config?.hollowBubbles?.legendPosition || DEFAULT_CONFIG.bubblesLegends.legendPosition,
    },
    fieldMap: ensureShowBubblesInFieldMap(config?.fieldMap),
  }

  if (!(FIELD_CALCULATIONS_NEW_CONFIG_KEY in config) && FIELD_CALCULATIONS_CONFIG_KEY in config) {
    // Need to update FIELD_CALCULATIONS_CONFIG_KEY to FIELD_CALCULATIONS_NEW_CONFIG_KEY
    // @ts-ignore TODO
    customConfig.fieldCalculationsV2Format = migrateFieldCalc(config.fieldCalculations)
  }

  return customConfig
}

function ensureShowBubblesInFieldMap (fieldMap: FieldMapItem[]) {
  // Ensure the leaf level always has "show as bubbles" enabled
  const fields = fieldMap.filter(f => f.level).sort((a, b) => (a.level || 0) - (b.level || 0))
  if (fields.length) {
    const maxLevelField = fields[fields.length - 1]
    if (!maxLevelField.showBubbles) {
      maxLevelField.showBubbles = true
    }
  }

  return fieldMap
}

function getVisualisationAppCBVConfig (): CBVConfig | {} {
  const isEmbedded = window !== window.top
  const isCCC = window.location.origin === process.env.VUE_APP_CBV_CCC_ORIGIN
  const isECAN = window.location.origin === process.env.VUE_APP_CBV_ECAN_ORIGIN
  if (isEmbedded && (isCCC || isECAN)) {
    return {
      splashScreen: { enabled: false },
      download: { enabled: false },
      searchSettings: { enabled: false },
      showMap: false,
      faq: { enabled: false },
      appConfig: {
        disableMobileBreadcrumb: true,
        disableMobileHamburger: true,
      },
    }
  } else {
    return {}
  }
}

// titleDescriptionGetters

export function stripHtml (html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export const nameof = <T>(name: Extract<keyof T, string>): string => name
